import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { Text } from "../../components/ui/Text";
import { useStore } from "../../context/StoreContext";
import { api } from "services/api";
import { getCountryByCode } from "../../utils/countryCurrencyData";

interface PaymentMethodConfig {
  cashOnDelivery: boolean;
  cashOnPickup: boolean;
  payByInvoice: boolean;
  onlinePayment: boolean;
}

export const PaymentMethodSettings: React.FC = () => {
  const { t } = useTranslation();
  const { store, isPluginEnabled, refreshStore, loading: storeLoading } = useStore();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodConfig>({
    cashOnDelivery: true,
    cashOnPickup: false,
    payByInvoice: true,
    onlinePayment: false,
  });

  const isOnlinePaymentEnabled = isPluginEnabled("online-payment");
  const loading = storeLoading;

  // Store locations (countries) available for payment method configuration
  const [storeLocations, setStoreLocations] = useState<Array<{ code: string; name: string }>>([]);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Get configured countries from store (filtered to only show countries in store locations)
  const configuredCountries = store?.paymentMethodsByCountry
    ? Object.keys(store.paymentMethodsByCountry).filter((countryCode) =>
        storeLocations.some((loc) => loc.code === countryCode)
      )
    : [];

  // Fetch store locations
  useEffect(() => {
    const fetchStoreLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await api.get("/locations/admin?isActive=true");
        const locations = response.data.locations || [];
        // Map locations to country codes and names
        const locationOptions = locations.map((loc: any) => {
          const country = getCountryByCode(loc.countryCode);
          return {
            code: loc.countryCode,
            name: country?.name || loc.country || loc.countryCode,
          };
        });
        setStoreLocations(locationOptions);
      } catch (err: any) {
        // Error is handled by setting error state
        setError(t("admin.paymentMethodSettings.errors.loadLocations"));
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchStoreLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize form fields when store data is loaded or country changes
  useEffect(() => {
    if (store && !storeLoading) {
      if (selectedCountryCode) {
        // Load country-specific payment methods
        const countryMethods = store.paymentMethodsByCountry?.[selectedCountryCode];
        if (countryMethods) {
          setPaymentMethods({
            cashOnDelivery: countryMethods.cashOnDelivery ?? true,
            cashOnPickup: countryMethods.cashOnPickup ?? false,
            payByInvoice: countryMethods.payByInvoice ?? true,
            onlinePayment: countryMethods.onlinePayment ?? false,
          });
        } else {
          // Use defaults for new country
          setPaymentMethods({
            cashOnDelivery: true,
            cashOnPickup: false,
            payByInvoice: true,
            onlinePayment: isOnlinePaymentEnabled,
          });
        }
      } else {
        // Use legacy store-level payment methods as defaults
        const legacyMethods = store.paymentMethods || {
          cashOnDelivery: true,
          cashOnPickup: false,
          payByInvoice: true,
          onlinePayment: isOnlinePaymentEnabled,
        };
        setPaymentMethods({
          cashOnDelivery: legacyMethods.cashOnDelivery ?? true,
          cashOnPickup: legacyMethods.cashOnPickup ?? false,
          payByInvoice: legacyMethods.payByInvoice ?? true,
          onlinePayment: legacyMethods.onlinePayment ?? false,
        });
      }
    }
  }, [store, storeLoading, selectedCountryCode, isOnlinePaymentEnabled]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!selectedCountryCode) {
      setError(t("admin.paymentMethodSettings.pleaseSelectCountry"));
      return;
    }

    setSaving(true);
    try {
      await api.put("/store/payment-methods", {
        countryCode: selectedCountryCode,
        cashOnDelivery: paymentMethods.cashOnDelivery,
        cashOnPickup: paymentMethods.cashOnPickup,
        payByInvoice: paymentMethods.payByInvoice,
        onlinePayment: isOnlinePaymentEnabled ? paymentMethods.onlinePayment : false,
      });
      setSuccess(t("admin.paymentMethodSettings.success"));
      await refreshStore();
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.paymentMethodSettings.errors.update"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCountry = async (countryCode: string) => {
    const countryName = getCountryByCode(countryCode)?.name || countryCode;
    if (!window.confirm(t("admin.paymentMethodSettings.removeCountryConfirm", { country: countryName }))) {
      return;
    }

    try {
      setError(null);
      // Get current payment methods for this country
      const currentMethods = store?.paymentMethodsByCountry?.[countryCode];
      if (currentMethods) {
        // Set all to false to effectively remove the country config
        await api.put("/store/payment-methods", {
          countryCode,
          cashOnDelivery: false,
          cashOnPickup: false,
          payByInvoice: false,
          onlinePayment: false,
        });
        await refreshStore();
        if (selectedCountryCode === countryCode) {
          setSelectedCountryCode("");
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.paymentMethodSettings.errors.removeCountry"));
    }
  };

  if (loading) {
    return (
      <Container>
        <Stack spacing="lg">
          <Spinner />
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <Heading size="xl">{t("admin.paymentMethodSettings.title")}</Heading>
        <Text className="text-gray-600 dark:text-gray-400">
          {t("admin.paymentMethodSettings.description")}
        </Text>

        {loadingLocations && (
          <Alert variant="info">
            <Text size="sm">{t("admin.paymentMethodSettings.loadingLocations")}</Text>
          </Alert>
        )}

        {!loadingLocations && storeLocations.length === 0 && (
          <Alert variant="warning">
            <Text size="sm">
              {t("admin.paymentMethodSettings.noLocationsFound")}
            </Text>
          </Alert>
        )}

        {error && <Alert variant="error">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {/* Configured Countries List */}
        {configuredCountries.length > 0 && (
          <Card>
            <Stack spacing="md">
              <Heading size="lg">{t("admin.paymentMethodSettings.configuredCountries")}</Heading>
              <div className="space-y-2">
                {configuredCountries.map((countryCode) => {
                  const country = getCountryByCode(countryCode);
                  const methods = store?.paymentMethodsByCountry?.[countryCode];
                  const enabledMethods = [
                    methods?.cashOnDelivery && t("admin.paymentMethodSettings.cashOnDelivery"),
                    methods?.cashOnPickup && t("admin.paymentMethodSettings.cashOnPickup"),
                    methods?.payByInvoice && t("admin.paymentMethodSettings.payByInvoice"),
                    methods?.onlinePayment && t("admin.paymentMethodSettings.onlinePayment"),
                  ].filter(Boolean);

                  return (
                    <div
                      key={countryCode}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded hover:bg-gray-50"
                    >
                      <div>
                        <Text weight="semibold">
                          {country?.name || countryCode} ({countryCode})
                        </Text>
                        <Text size="sm" className="text-gray-600">
                          {enabledMethods.length > 0
                            ? `${t("admin.paymentMethodSettings.enabled")}: ${enabledMethods.join(", ")}`
                            : t("admin.paymentMethodSettings.noPaymentMethodsEnabled")}
                        </Text>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedCountryCode(countryCode)}
                        >
                          {t("admin.paymentMethodSettings.edit")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteCountry(countryCode)}
                        >
                          {t("admin.paymentMethodSettings.remove")}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Stack>
          </Card>
        )}

        {/* Payment Method Configuration Form */}
        <Card>
          <form onSubmit={handleSubmit}>
            <Stack spacing="lg">
              {/* Country Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("admin.paymentMethodSettings.country")} <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={selectedCountryCode}
                  onChange={(e) => setSelectedCountryCode(e.target.value)}
                  disabled={storeLocations.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">{t("admin.paymentMethodSettings.selectCountry")}</option>
                  {storeLocations.map((location) => (
                    <option key={location.code} value={location.code}>
                      {location.name} ({location.code})
                    </option>
                  ))}
                </select>
                {storeLocations.length === 0 && (
                  <p className="mt-1 text-sm text-red-500">
                    {t("admin.paymentMethodSettings.noLocationsAvailable")}
                  </p>
                )}
                {selectedCountryCode && (
                  <p className="mt-1 text-sm text-gray-500">
                    {t("admin.paymentMethodSettings.selected")}: {getCountryByCode(selectedCountryCode)?.name || selectedCountryCode}
                  </p>
                )}
              </div>

              <div className="space-y-4">
                {/* Cash on Delivery */}
                <div className="flex items-start justify-between p-4 border border-gray-200 rounded">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="cashOnDelivery"
                        checked={paymentMethods.cashOnDelivery}
                        onChange={(e) =>
                          setPaymentMethods({
                            ...paymentMethods,
                            cashOnDelivery: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="cashOnDelivery" className="cursor-pointer">
                        <Text weight="semibold">
                          {t("admin.paymentMethodSettings.cashOnDelivery")}
                        </Text>
                        <Text size="sm" className="text-gray-600 dark:text-gray-400">
                          {t("admin.paymentMethodSettings.cashOnDeliveryDesc")}
                        </Text>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Cash on Pickup */}
                <div className="flex items-start justify-between p-4 border border-gray-200 rounded">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="cashOnPickup"
                        checked={paymentMethods.cashOnPickup}
                        onChange={(e) =>
                          setPaymentMethods({
                            ...paymentMethods,
                            cashOnPickup: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="cashOnPickup" className="cursor-pointer">
                        <Text weight="semibold">{t("admin.paymentMethodSettings.cashOnPickup")}</Text>
                        <Text size="sm" className="text-gray-600 dark:text-gray-400">
                          {t("admin.paymentMethodSettings.cashOnPickupDesc")}
                        </Text>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Pay by Invoice */}
                <div className="flex items-start justify-between p-4 border border-gray-200 rounded">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="payByInvoice"
                        checked={paymentMethods.payByInvoice}
                        onChange={(e) =>
                          setPaymentMethods({
                            ...paymentMethods,
                            payByInvoice: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="payByInvoice" className="cursor-pointer">
                        <Text weight="semibold">
                          {t("admin.paymentMethodSettings.payByInvoice")}
                        </Text>
                        <Text size="sm" className="text-gray-600 dark:text-gray-400">
                          {t("admin.paymentMethodSettings.payByInvoiceDesc")}
                        </Text>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Online Payment */}
                <div className="flex items-start justify-between p-4 border border-gray-200 rounded">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="onlinePayment"
                        checked={paymentMethods.onlinePayment}
                        onChange={(e) =>
                          setPaymentMethods({
                            ...paymentMethods,
                            onlinePayment: e.target.checked,
                          })
                        }
                        disabled={!isOnlinePaymentEnabled}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label htmlFor="onlinePayment" className="cursor-pointer">
                        <Text weight="semibold">
                          {t("admin.paymentMethodSettings.onlinePayment")}
                        </Text>
                        <Text size="sm" className="text-gray-600 dark:text-gray-400">
                          {t("admin.paymentMethodSettings.onlinePaymentDesc")}
                        </Text>
                        {!isOnlinePaymentEnabled && (
                          <Alert variant="warning" className="mt-2">
                            <Text size="sm">
                              {t("admin.paymentMethodSettings.onlinePaymentRequired")}
                            </Text>
                          </Alert>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button 
                  type="submit" 
                  loading={saving} 
                  disabled={saving || !selectedCountryCode || storeLocations.length === 0}
                >
                  {selectedCountryCode &&
                  store?.paymentMethodsByCountry?.[selectedCountryCode]
                    ? t("admin.paymentMethodSettings.updateConfiguration")
                    : t("admin.paymentMethodSettings.saveConfiguration")}
                </Button>
              </div>
            </Stack>
          </form>
        </Card>
      </Stack>
    </Container>
  );
};
